import { Tab } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { PageHeader, QueryTabs, QueryTabPanel } from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { ManageShop, ShopIntegrations } from 'features/shop';
import { useAppHistory, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

enum ShopDetailsTab {
  Shop = 'shop',
  Integrations = 'integrations',
}

export const ShopDetailsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });

  const { isAdmin, isTechOperator, isMerchant, isAdminOrTechOperator } =
    useUser();

  const { goBack } = useAppHistory();

  const { id: shopId = '' } = useParams<{ id: string }>();
  const isNew = useMemo(() => shopId === NEW_ID, [shopId]);
  const title = useMemo(
    () => t(isNew ? 'new_shop.title' : 'edit_shop.title'),
    [isNew, t],
  );

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SHOPS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SHOPS;
    } else if (isMerchant) {
      return ROUTE_PATH.MERCHANT.SHOPS;
    }
  }, [isAdmin, isTechOperator, isMerchant]);

  const navigateBack = useCallback(() => {
    goBack(backUrl!);
  }, [backUrl, goBack]);

  const hideTabs = useMemo(
    () => isNew || !isAdminOrTechOperator,
    [isNew, isAdminOrTechOperator],
  );

  const tabProps = useMemo(
    () => ({
      shopId,
      navigateBack,
    }),
    [shopId, navigateBack],
  );

  if (hideTabs) {
    return (
      <div>
        <PageHeader title={title} />
        <ManageShop {...tabProps} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={title} divider={false} />
      <QueryTabs tabsEnum={ShopDetailsTab}>
        <Tab value={ShopDetailsTab.Shop} label={t('edit_shop.tabs.shop')} />
        {isAdminOrTechOperator && (
          <Tab
            value={ShopDetailsTab.Integrations}
            label={t('edit_shop.tabs.integrations')}
          />
        )}
      </QueryTabs>
      <QueryTabPanel value={ShopDetailsTab.Shop}>
        <ManageShop {...tabProps} />
      </QueryTabPanel>
      {isAdminOrTechOperator && (
        <QueryTabPanel value={ShopDetailsTab.Integrations}>
          <ShopIntegrations {...tabProps} />
        </QueryTabPanel>
      )}
    </div>
  );
};
